<script module lang="ts">
  import { QUAD, quadMeta, type Quadrant } from '@ui/quadrant';

  export interface MemberPoint {
    member_id: number;
    name: string;
    handle?: string | null;
    picture_profile?: string | null;
    activity: number;
    value_per_contribution: number;
    upvotes_received: number;
    quadrant: string;
    stage?: string;
  }
</script>

<script lang="ts">
  import Avatar from '@ui/Avatar.svelte';
  import MemberHover from '@ui/MemberHover.svelte';
  import { PanZoom } from './panzoom.svelte';
  import { STAGE_ORDER, STAGE_META, stageMeta, type Stage } from '@ui/stages';

  let {
    points,
    xMedian,
    yMedian,
    total = null,
    onpick = null,
    colorBy = 'quadrant',
    quadrantFills = false,
  }: {
    points: MemberPoint[];
    xMedian: number;
    yMedian: number;
    total?: number | null;
    onpick?: ((p: MemberPoint) => void) | null;
    // What the dot colour encodes: the value/volume quadrant (standing), or the
    // momentum stage (trajectory) laid over the same map.
    colorBy?: 'quadrant' | 'stage';
    // Tint the four value/volume regions by their role colour (so position reads as
    // the quadrant even when dots are coloured by something else, e.g. momentum).
    quadrantFills?: boolean;
  } = $props();

  // Dot colour + aria label, by mode. Position (the quadrant the dot falls in) is
  // unchanged; only the colour meaning switches. Every dot is a current member (the
  // API plots that population only), so `stageMeta` never returns null here — the
  // fallback is for an unrecognised stage, not for a departed one.
  const stageOf = (p: MemberPoint) => stageMeta(p.stage) ?? STAGE_META.silent;
  const dotColor = (p: MemberPoint): string =>
    colorBy === 'stage' ? stageOf(p).color : quadMeta(p.quadrant).color;
  const dotLabel = (p: MemberPoint): string =>
    colorBy === 'stage' ? stageOf(p).label : quadMeta(p.quadrant).label;

  const PLOT_H = 460;
  const PAD = 34; // keeps edge markers off the frame
  const MARGIN = 44; // cull radius beyond the viewport
  const AVATAR_ZOOM = 1.9; // dots below this, avatars at/above
  const AVATAR_CAP = 350; // max avatars rendered at once (most-active kept)

  let plotEl = $state<HTMLDivElement>();
  let plotW = $state(600);
  const pz = new PanZoom(() => plotW, PLOT_H);

  // Wheel must be a non-passive listener so we can preventDefault the page scroll.
  $effect(() => {
    const el = plotEl;
    if (!el) return;
    el.addEventListener('wheel', pz.onWheel, { passive: false });
    return () => el.removeEventListener('wheel', pz.onWheel);
  });

  const tx = (v: number) => Math.log10(1 + Math.max(0, v));

  const xDomain = $derived.by(() => {
    const vals = points.map((p) => tx(p.activity));
    vals.push(tx(xMedian));
    let min = Math.min(...vals), max = Math.max(...vals);
    if (min === max) { min -= 1; max += 1; }
    return { min, max };
  });
  const yDomain = $derived.by(() => {
    const vals = points.map((p) => p.value_per_contribution);
    vals.push(yMedian);
    let min = Math.min(...vals), max = Math.max(...vals);
    if (min === max) { min -= 1; max += 1; }
    return { min, max };
  });

  const baseX = (v: number) => PAD + ((tx(v) - xDomain.min) / (xDomain.max - xDomain.min)) * (plotW - 2 * PAD);
  const baseY = (v: number) => PLOT_H - PAD - ((v - yDomain.min) / (yDomain.max - yDomain.min)) * (PLOT_H - 2 * PAD);
  const screenX = (v: number) => pz.panX + baseX(v) * pz.scale;
  const screenY = (v: number) => pz.panY + baseY(v) * pz.scale;

  // Members with identical (activity, value) would draw exactly on top of each
  // other — so the dot count looks lower than the stats. Fan any such cluster out
  // in a small ring (base-pixel offset, so it scales with zoom) to keep the count
  // visible without meaningfully moving the point.
  const jitter = $derived.by(() => {
    const groups = new Map<string, number[]>();
    for (const p of points) {
      const k = `${p.activity}|${p.value_per_contribution}`;
      (groups.get(k) ?? groups.set(k, []).get(k)!).push(p.member_id);
    }
    const off = new Map<number, [number, number]>();
    for (const ids of groups.values()) {
      if (ids.length < 2) continue;
      const R = 7;
      ids.forEach((id, i) => {
        const a = (2 * Math.PI * i) / ids.length;
        off.set(id, [Math.cos(a) * R, Math.sin(a) * R]);
      });
    }
    return off;
  });

  // Stage visibility toggles (momentum mode): click a legend chip to hide/show that
  // bucket's dots. Domain/medians stay computed from ALL points, so toggling filters
  // without rescaling the plot.
  let hidden = $state<Set<string>>(new Set());
  function toggleStage(s: string): void {
    const next = new Set(hidden);
    next.has(s) ? next.delete(s) : next.add(s);
    hidden = next;
  }
  const shownPoints = $derived(
    colorBy === 'stage' && hidden.size ? points.filter((p) => !hidden.has(p.stage ?? '')) : points,
  );

  const markers = $derived(
    shownPoints.map((p) => {
      const [jx, jy] = jitter.get(p.member_id) ?? [0, 0];
      return {
        p,
        sx: screenX(p.activity) + jx * pz.scale,
        sy: screenY(p.value_per_contribution) + jy * pz.scale,
      };
    }),
  );
  const visible = $derived(
    markers.filter((m) => m.sx >= -MARGIN && m.sx <= plotW + MARGIN && m.sy >= -MARGIN && m.sy <= PLOT_H + MARGIN),
  );
  const showAvatars = $derived(pz.scale >= AVATAR_ZOOM);
  // Points are pre-sorted most-notable-first by the server, so slicing keeps the best.
  const rendered = $derived(showAvatars ? visible.slice(0, AVATAR_CAP) : visible);
  const medianX = $derived(screenX(xMedian));
  const medianY = $derived(screenY(yMedian));

  // Four tinted regions split at the medians (screen top = high value). Clamped to
  // the viewport so they still fill when the crosshair is panned off-screen.
  const fills = $derived.by(() => {
    if (!quadrantFills) return [];
    const mx = Math.max(0, Math.min(plotW, medianX));
    const my = Math.max(0, Math.min(PLOT_H, medianY));
    return [
      { q: 'quiet_expert', l: 0, t: 0, w: mx, h: my },
      { q: 'key_voice', l: mx, t: 0, w: plotW - mx, h: my },
      { q: 'spectator', l: 0, t: my, w: mx, h: PLOT_H - my },
      { q: 'broadcaster', l: mx, t: my, w: plotW - mx, h: PLOT_H - my },
    ] satisfies { q: Quadrant; l: number; t: number; w: number; h: number }[];
  });

  const counts = $derived.by(() => {
    // `departed` is here to keep the record total — it stays 0 and is never shown:
    // the server leaves ex-members out of the aggregate entirely, because a member
    // who left has no current value/volume position to plot. Only the four corner
    // quadrants below read out of this map.
    const c: Record<Quadrant, number> = {
      quiet_expert: 0, key_voice: 0, broadcaster: 0, spectator: 0, newcomer: 0, departed: 0,
    };
    for (const p of points) if (p.quadrant in c) c[p.quadrant as Quadrant]++;
    return c;
  });
  const stageCounts = $derived.by(() => {
    // Live stages only, unlike `counts` above: `Stage` excludes the terminal
    // `departed` marker by design, and the legend below reads this map through
    // STAGE_ORDER, so a departed bucket would never be shown even if it were here.
    // The `in c` guard is therefore what drops an ex-member's stored `departed`
    // marker instead of miscounting it as one of the four.
    const c: Record<Stage, number> = { rising: 0, steady: 0, at_risk: 0, silent: 0 };
    for (const p of points) if (p.stage && p.stage in c) c[p.stage as Stage]++;
    return c;
  });

</script>

<div class="relative">
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div
    bind:this={plotEl}
    bind:clientWidth={plotW}
    class="relative overflow-hidden rounded-box border hair bg-base-100 {pz.dragging ? 'cursor-grabbing' : 'cursor-grab'}"
    style:height="{PLOT_H}px"
    style:touch-action="none"
    onpointerdown={pz.onPointerDown}
    onpointermove={pz.onPointerMove}
    onpointerup={pz.onPointerUp}
    onpointercancel={pz.onPointerUp}
  >
    <!-- zoom controls (anchored to the plot; stop drag on interaction) -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div class="absolute right-2 top-2 z-20 flex flex-col gap-1" onpointerdown={(e) => e.stopPropagation()}>
      <button class="btn btn-xs btn-square border hair bg-base-100/80" onclick={() => pz.zoomAt(plotW / 2, PLOT_H / 2, 1.4)} aria-label="Zoom in">+</button>
      <button class="btn btn-xs btn-square border hair bg-base-100/80" onclick={() => pz.zoomAt(plotW / 2, PLOT_H / 2, 1 / 1.4)} aria-label="Zoom out">−</button>
      <button class="btn btn-xs btn-square border hair bg-base-100/80 font-mono text-[0.55rem]" onclick={() => pz.reset()} aria-label="Reset zoom">⤢</button>
    </div>

    <!-- quadrant fills (role regions) -->
    {#each fills as f (f.q)}
      <div class="pointer-events-none absolute" style="left:{f.l}px; top:{f.t}px; width:{f.w}px; height:{f.h}px; background: color-mix(in oklab, {QUAD[f.q].color} 9%, transparent)"></div>
    {/each}

    <!-- median guides -->
    <div class="pointer-events-none absolute top-0 bottom-0 border-l border-dashed border-base-content/25" style:left="{medianX}px"></div>
    <div class="pointer-events-none absolute left-0 right-0 border-t border-dashed border-base-content/25" style:top="{medianY}px"></div>

    <!-- corner quadrant chips (name + count; anchored to the plot's corners) -->
    {#each [['quiet_expert', 'left-2 top-2'], ['key_voice', 'right-12 top-2'], ['spectator', 'left-2 bottom-2'], ['broadcaster', 'right-2 bottom-2']] as [q, pos] (q)}
      {@const meta = QUAD[q as Quadrant]}
      <div class="pointer-events-none absolute z-10 {pos}">
        <span class="inline-flex items-center gap-1.5 rounded-md border hair bg-base-100/80 px-2 py-1 text-[0.72rem] font-semibold shadow-sm backdrop-blur-sm" style:color={meta.color}>
          <span class="inline-block h-2.5 w-2.5 rounded-sm" style:background={meta.color}></span>{meta.label}
          <span class="tabular-nums text-base-content/65">{counts[q as Quadrant]}</span>
        </span>
      </div>
    {/each}

    <!-- markers — each wrapped in the shared member hovercard, anchored to the dot -->
    {#each rendered as m (m.p.member_id)}
      {@const color = dotColor(m.p)}
      <MemberHover
        memberId={m.p.member_id}
        class="absolute left-0 top-0"
        style="transform: translate({m.sx}px, {m.sy}px) translate(-50%, -50%); will-change: transform;"
      >
        <button
          class="rounded-full focus:outline-none focus:ring-2 focus:ring-primary/50"
          onclick={() => { if (!pz.dragMoved) onpick?.(m.p); }}
          aria-label="{m.p.name} — {dotLabel(m.p)}"
        >
          {#if showAvatars}
            <span class="block h-9 w-9 rounded-full leading-none" style:box-shadow="0 0 0 3px {color}">
              <Avatar src={m.p.picture_profile ?? null} name={m.p.name} size={36} />
            </span>
          {:else}
            <span class="block h-3.5 w-3.5 rounded-full ring-[1.5px] ring-base-100/70" style:background={color}></span>
          {/if}
        </button>
      </MemberHover>
    {/each}

    <!-- axis labels -->
    <span class="pointer-events-none absolute bottom-1.5 left-1/2 -translate-x-1/2 rounded bg-base-100/70 px-1.5 py-0.5 font-mono text-[0.65rem] text-base-content/72 backdrop-blur-sm">volume (contributions) →</span>
    <span class="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 -rotate-90 rounded bg-base-100/70 px-1.5 py-0.5 font-mono text-[0.65rem] text-base-content/72 backdrop-blur-sm">value (▲ / contribution) →</span>
  </div>

  {#if colorBy === 'stage'}
    <!-- momentum filter: tick a bucket to show/hide its dots -->
    <div class="eyebrow mt-3 mb-2 text-[0.6rem] text-base-content/62">Filter dots by momentum</div>
    <div class="flex flex-wrap gap-x-6 gap-y-3">
      {#each STAGE_ORDER as s (s)}
        {@const off = hidden.has(s)}
        <label class="inline-flex cursor-pointer select-none items-center gap-2 text-lg font-semibold transition-opacity {off ? 'opacity-45' : ''}">
          <input
            type="checkbox"
            class="h-5 w-5 cursor-pointer rounded"
            style="accent-color: {STAGE_META[s].color}"
            checked={!off}
            onchange={() => toggleStage(s)}
          />
          <span style:color={STAGE_META[s].color}>{STAGE_META[s].label}</span>
          <span class="font-mono text-sm text-base-content/65">{stageCounts[s]}</span>
        </label>
      {/each}
    </div>
  {/if}

  <div class="mt-1.5 flex items-center justify-between font-mono text-[0.6rem] text-base-content/62">
    <span>{total ?? points.length} contributors · {showAvatars ? 'avatars' : 'dots — scroll to zoom in for faces'}{rendered.length < visible.length ? ` · showing ${rendered.length} of ${visible.length} in view` : ''}</span>
    <span>scroll / drag to explore · {Math.round(pz.scale * 100)}%</span>
  </div>
</div>
